import { extend } from 'umi-request'
import _ from 'lodash'
import md5 from 'js-md5'
import baseUrl from '../config/env'
import { emitAuthRequired } from '../util/authEvents'
import {
    AUTH_CODE,
    getServerAuthStore,
    isAuthRequiredCode,
    normalizeAuthCode,
    setServerAuthRequiredCode,
} from '../util/authContext'
import {
    getExplorerInfo,
    getOrCreateDeviceId,
    getSystemVersion,
    readAuthFromCookie,
    readBrowserAuth,
} from '../util/auth'

const AUTH_USER_ID_KEY = 'userId'

const LEGACY_API_REWRITES = [
    [/^\/new\/lottery\/jczqodds\/getIntelligenceLeagueList$/, '/cloudWebop/lottery/jczqodds/getIntelligenceLeagueListNew'],
    [/^\/new\/lottery\/jczqodds\/getIntelligenceMatchList$/, '/cloudWebop/lottery/jczqodds/getIntelligenceMatchListNew'],
    [/^\/new\/lottery\/jczqodds\/getTodayInGameStatusMatchList$/, '/cloudWebop/lottery/jczqodds/getTodayInGameStatusMatchListNew'],
    [/^\/new\/lottery\/jczqodds\/getMatchIntelligence$/, '/cloudWebop/lottery/jczqodds/getMatchIntelligenceNew'],
    [/^\/new\/zlk\/getLeagues$/, '/cloudWebop/zlk/getLeaguesNew'],
    [/^\/new\/zlk\/getScores$/, '/cloudWebop/zlk/getScoresNew'],
    [/^\/new\/zlk\/getLeagueInfo$/, '/cloudWebop/zlk/getLeagueInfoNew'],
    [/^\/new\/zlk\/teamSkill$/, '/cloudWebop/zlk/teamSkillNew'],
    [/^\/new\/zlk\/playerSkill$/, '/cloudWebop/zlk/playerSkillNew'],
    [/^\/new\/zlk\/schedules$/, '/cloudWebop/zlk/schedulesNew'],
    [/^\/new\/zlk\/player\/getPlayerDetail$/, '/cloudWebop/zlk/player/getPlayerDetailNew'],
    [/^\/new\/zlk\/team\/getTeamInfoById$/, '/cloudWebop/zlk/team/getTeamInfoByIdNew'],
    [/^\/new\/zlk\/team\/getLineupList$/, '/cloudWebop/zlk/team/getLineupListNew'],
    [/^\/new\/zlk\/team\/getPlayerList$/, '/cloudWebop/zlk/team/getPlayerListNew'],
    [/^\/new\/zlk\/team\/getPlayerMatchSeason$/, '/cloudWebop/zlk/team/getPlayerMatchSeasonNew'],
    [/^\/new\/zlk\/team\/getTeamRecentYearsScores$/, '/cloudWebop/zlk/team/getTeamRecentYearsScoresNew'],
    [/^\/new\/zlk\/team\/getTeamScheduleList$/, '/cloudWebop/zlk/team/getTeamScheduleListNew'],
    [/^\/new\/zlk\/team\/getTeamFourFutureSchedule$/, '/cloudWebop/zlk/team/getTeamFourFutureScheduleNew'],
    [/^\/new\/zlk\/team\/getTransferInRecordList$/, '/cloudWebop/zlk/team/getTransferInRecordListNew'],
    [/^\/new\/zlk\/team\/getTransferOutRecordList$/, '/cloudWebop/zlk/team/getTransferOutRecordListNew'],
    [/^\/new\/website\/lottery\//, '/cloudWebop/website/lottery/'],
    [/^\/new\/website\/analysis\//, '/cloudWebop/website/analysis/'],
    [/^\/new\/website\/real\/time\//, '/cloudWebop/website/real/time/'],
    [/^\/new\/website\/end\/time\//, '/cloudWebop/website/end/time/'],
    [/^\/new\/website\/tomorrow\/time\//, '/cloudWebop/website/tomorrow/time/'],
    [/^\/new\/website\/information\//, '/cloudWebop/website/information/'],
    [/^\/new\/lottery\/jczqodds\//, '/cloudWebop/lottery/jczqodds/'],
    [/^\/new\/zlk\//, '/cloudWebop/zlk/'],
    [/^\/new\/match\//, '/cloudMatch/'],
]

const request = extend({
    prefix: baseUrl,
    timeout: 300000000,
    errorHandler: error => {
        try {
            console.log('error', error)
        } catch (e) {}
        throw error
    },
})

export class AuthRequiredError extends Error {
    constructor(code, response) {
        super(`AUTH_REQUIRED_${code}`)
        this.name = 'AuthRequiredError'
        this.code = Number(code)
        this.response = response
    }
}

export function rewriteApiUrl(url) {
    for (const [pattern, replacement] of LEGACY_API_REWRITES) {
        if (pattern.test(url)) {
            return url.replace(pattern, replacement)
        }
    }
    return url
}

function getRequestAuth(headers = {}, options = {}) {
    if (options.auth) {
        return options.auth
    }

    const serverStore = getServerAuthStore()
    if (serverStore) {
        return readAuthFromCookie(serverStore.cookie || '')
    }

    const cookieHeader = headers.Cookie || headers.cookie
    if (cookieHeader) {
        return readAuthFromCookie(cookieHeader)
    }

    return readBrowserAuth()
}

function getRequestUserAgent(headers = {}) {
    const serverStore = getServerAuthStore()
    const serverHeaders = serverStore ? serverStore.headers || {} : {}
    return headers['user-agent'] || headers['User-Agent'] || serverHeaders['user-agent'] || ''
}

function getDeviceParams(headers = {}, auth = {}) {
    const isBrowser = typeof window !== 'undefined'
    const userAgent = getRequestUserAgent(headers) || (isBrowser ? window.navigator.userAgent : '')
    const explorer = getExplorerInfo(userAgent)

    return {
        device_id: auth.deviceId || (isBrowser ? getOrCreateDeviceId() : ''),
        deviceId: auth.deviceId || (isBrowser ? getOrCreateDeviceId() : ''),
        systemVersion: getSystemVersion(userAgent),
        deviceModel: explorer.version,
        deviceBrand: explorer.type,
    }
}

function normalizeBusinessData(data = {}) {
    const source = data && typeof data === 'object' ? data : {}
    const nestedParams = source.params && typeof source.params === 'object' ? source.params : null
    const businessData = {
        ...source,
        ...(nestedParams || {}),
    }

    delete businessData.params
    return businessData
}

export function getSign(obj = {}) {
    const newObj = JSON.parse(JSON.stringify(obj))
    for (const key in newObj) {
        if (
            (!newObj[key] && newObj[key] !== 0) ||
            JSON.stringify(newObj[key]) === '{}' ||
            Array.isArray(newObj[key])
        ) {
            delete newObj[key]
        }
    }

    const keys = Object.keys(newObj).sort()
    let values = ''
    keys.forEach(key => {
        values += `${newObj[key]}&`
    })

    return md5(`${values}FE1C636C5A855EA4`).toUpperCase()
}

export function buildCommonParams(data = {}, headers = {}, options = {}) {
    const auth = getRequestAuth(headers, options)
    const userParams = auth.token
        ? {
            user_id: Number(auth.userId || auth[AUTH_USER_ID_KEY] || 0),
            userId: Number(auth.userId || auth[AUTH_USER_ID_KEY] || 0),
            token: auth.token,
        }
        : {}
    const params = {
        device_type: 1,
        version: '6.5.4',
        platform: 'web',
        v: '1',
        sid: 9001,
        ...getDeviceParams(headers, auth),
        ...userParams,
        ...normalizeBusinessData(data),
        timeInMillis: new Date().getTime(),
        timeZone: new Date().toTimeString().split(' ')[1],
    }

    params.sign = getSign(params)
    return params
}

function handleAuthRequired(response, options = {}) {
    const code = normalizeAuthCode(response && response.code)
    if (!code || options.skipAuthBlock || !isAuthRequiredCode(code)) {
        return response
    }

    setServerAuthRequiredCode(code)
    emitAuthRequired(code)
    if (code === AUTH_CODE.REAL_NAME_FAILED) {
        return {
            ...(response && typeof response === 'object' ? response : {}),
            code,
            data: [],
            authRequired: true,
        }
    }
    throw new AuthRequiredError(code, response)
}

function ensureResponse(response) {
    if (response === undefined || response === null) {
        throw new Error('接口返回为空')
    }
    return response
}

function isAuthRequiredError(error) {
    return error instanceof AuthRequiredError ||
        (error && error.name === 'AuthRequiredError')
}

function handleAuthRequiredRejection(promise) {
    return promise.catch(error => {
        if (!isAuthRequiredError(error)) {
            throw error
        }
        if (typeof window === 'undefined') {
            throw error
        }
        return new Promise(() => {})
    })
}

export const post = (url, data = {}, headers = {}, options = {}) => {
    const requestUrl = rewriteApiUrl(url)
    const params = buildCommonParams(data, headers, options)

    const requestPromise = request.post(requestUrl, {
        data: { params },
        headers,
    }).then(response => {
        ensureResponse(response)
        handleAuthRequired(response, options)
        return response
    }).catch(error => {
        if (isAuthRequiredError(error)) {
            throw error
        }
        console.log('url', JSON.stringify(url))
        console.log('data', data)
        console.log('error', error)
        throw error
    })
    return handleAuthRequiredRejection(requestPromise)
}

export const formPost = (url, data = {}, options = {}) => {
    const requestUrl = rewriteApiUrl(url)
    const params = buildCommonParams(data, {}, options)

    const requestPromise = request.post(requestUrl, {
        data: params,
        requestType: 'form',
    }).then(response => {
        ensureResponse(response)
        handleAuthRequired(response, options)
        return response
    }).catch(error => {
        if (isAuthRequiredError(error)) {
            throw error
        }
        console.log('url', url)
        console.log('data', data)
        console.log('error', error)
        throw error
    })
    return handleAuthRequiredRejection(requestPromise)
}

function isBlobValue(value) {
    return typeof Blob !== 'undefined' && value instanceof Blob
}

export const filePost = (url, data, options = {}) => {
    const requestUrl = rewriteApiUrl(url)
    const formData = new FormData()
    const businessData = {}

    if (data && typeof data.forEach === 'function') {
        data.forEach((value, key) => {
            if (isBlobValue(value)) {
                formData.append(key, value)
            } else if (value !== undefined && value !== null && typeof value !== 'object') {
                businessData[key] = value
            }
        })
    }

    const params = buildCommonParams(businessData, {}, options)
    Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
            formData.append(key, String(params[key]))
        }
    })

    const requestPromise = request.post(requestUrl, {
        data: formData,
        headers: {},
    }).then(response => {
        ensureResponse(response)
        handleAuthRequired(response, options)
        return response
    }).catch(error => {
        if (isAuthRequiredError(error)) {
            throw error
        }
        console.log('url', url)
        console.log('data', data)
        console.log('error', error)
        throw error
    })
    return handleAuthRequiredRejection(requestPromise)
}
